import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { api_get, api_post, api_put, api_delete, apiError } from "../../lib/api";
import ImageUploader from "../../components/ImageUploader";
import SmartImage from "../../components/SmartImage";

const blank = {
    title: "",
    subtitle: "",
    imageUrl: "",
    imageAlt: "",
    linkUrl: "/destinations",
    isActive: true,
    showOnHome: true,
    cardSize: "small",
    displayOrder: 100,
};

const ManagePopularDestinations = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(blank);

    const load = async () => {
        try {
            const r = await api_get("/admin/popular-destinations");
            setItems(r);
        } catch (err) {
            toast.error(apiError(err));
        }
    };
    useEffect(() => {
        load();
    }, []);

    const open = (it = null) => {
        setEditing(it ? it.id : "new");
        setForm(it ? { ...blank, ...it } : blank);
    };
    const close = () => setEditing(null);

    const save = async (e) => {
        e.preventDefault();
        if (!form.title) return toast.error("Title required.");
        try {
            const payload = { ...form, displayOrder: Number(form.displayOrder) || 100 };
            if (editing === "new") await api_post("/admin/popular-destinations", payload);
            else await api_put(`/admin/popular-destinations/${editing}`, payload);
            toast.success("Saved.");
            close();
            await load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this popular card?")) return;
        try {
            await api_delete(`/admin/popular-destinations/${id}`);
            toast.success("Deleted.");
            await load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    return (
        <div data-testid="manage-popular">
            <header className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-display text-2xl text-ink font-bold">Popular destinations (Home)</h1>
                    <p className="text-ink-light text-sm">Bento cards on the Home page · controls visibility, order and size.</p>
                </div>
                <button onClick={() => open()} className="px-4 py-2 rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-semibold text-sm flex items-center gap-1.5 shadow-sm" data-testid="add-popular">
                    <Plus className="w-4 h-4" /> New card
                </button>
            </header>

            <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-cream-100 text-ink-light">
                        <tr>
                            <th className="text-left px-4 py-3 font-semibold">Image</th>
                            <th className="text-left px-4 py-3 font-semibold">Title / Link</th>
                            <th className="text-left px-4 py-3 font-semibold">Size</th>
                            <th className="text-left px-4 py-3 font-semibold">Order</th>
                            <th className="text-left px-4 py-3 font-semibold">Visibility</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it) => (
                            <tr key={it.id} className="border-t border-cream-200">
                                <td className="px-4 py-3">
                                    <div className="w-14 h-14 rounded-md overflow-hidden bg-cream-100">
                                        <SmartImage src={it.imageUrl} alt={it.title} className="w-full h-full" />
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="font-semibold text-ink">{it.title}</div>
                                    <div className="text-xs text-ink-light">{it.linkUrl}</div>
                                </td>
                                <td className="px-4 py-3 text-xs uppercase tracking-wider">{it.cardSize}</td>
                                <td className="px-4 py-3">{it.displayOrder}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${it.isActive && it.showOnHome ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                                        {it.isActive && it.showOnHome ? "Visible" : "Hidden"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => open(it)} className="p-1.5 rounded hover:bg-cream-100" data-testid={`edit-pop-${it.id}`}>
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => remove(it.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`delete-pop-${it.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={close}>
                    <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6 shadow-xl">
                        <header className="flex items-center justify-between mb-5">
                            <h2 className="font-display text-xl font-bold">{editing === "new" ? "New card" : "Edit card"}</h2>
                            <button type="button" onClick={close}><X className="w-5 h-5" /></button>
                        </header>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Title *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="pop-title" />
                            <Field label="Subtitle" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} />
                            <div className="md:col-span-2">
                                <ImageUploader value={form.imageUrl} onChange={(v) => setForm({ ...form, imageUrl: v })} testId="pop-image" />
                            </div>
                            <Field label="Image alt (SEO)" value={form.imageAlt} onChange={(v) => setForm({ ...form, imageAlt: v })} />
                            <Field label="Link URL" value={form.linkUrl} onChange={(v) => setForm({ ...form, linkUrl: v })} />
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Card size</label>
                                <select value={form.cardSize} onChange={(e) => setForm({ ...form, cardSize: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-cream-200 bg-white text-sm">
                                    <option value="large">Large (2×2)</option>
                                    <option value="medium">Medium (2×1)</option>
                                    <option value="small">Small (1×1)</option>
                                </select>
                            </div>
                            <Field label="Display order" type="number" value={form.displayOrder} onChange={(v) => setForm({ ...form, displayOrder: Number(v) })} />
                            <div className="flex items-center gap-4 mt-7">
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                                    Active
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.showOnHome} onChange={(e) => setForm({ ...form, showOnHome: e.target.checked })} />
                                    Show on Home
                                </label>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button type="button" onClick={close} className="px-4 py-2 rounded-lg border border-cream-200">Cancel</button>
                            <button type="submit" className="px-5 py-2 rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-semibold" data-testid="pop-save">Save</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

const Field = ({ label, value, onChange, type = "text", testId }) => (
    <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">{label}</label>
        <input
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-cream-200 bg-white text-sm"
            data-testid={testId}
        />
    </div>
);

export default ManagePopularDestinations;
